<script>
	import { tweened } from 'svelte/motion';
	import { cubicOut } from 'svelte/easing';
	import { fade } from 'svelte/transition';
	import * as Pancake from '@sveltejs/pancake';
	import points from './points.js';
	import { resampleMonotone } from '../lib/monotone-resample.js';

	// ---- assessment state -------------------------------------------------
	let q1, q2, q3;

	$: answered = [q1, q2, q3].filter((v) => v !== undefined).length;
	$: complete = answered === 3;
	$: totalscore = complete ? q1 + q2 + q3 : NaN;

	// points.js is a cumulative curve: inty at a score is the estimated
	// proportion of treated hemifacial-spasm patients scoring at or below it
	// (Tambasco et al. 2019). That makes it a percentile, which is the
	// plain-language fact patients can actually use.
	function cdf(x) {
		for (const row of points) if (+row.score === x) return +row.inty;
		return null;
	}

	// The curve is cumulative, so this share is always the share the patient
	// scores ABOVE. Phrasing it any other way inverts the meaning: at score 3
	// the value is 25, and "lower than about 25%" states the opposite of the
	// truth. One direction, always correct, repeatable to a doctor.
	// points.js has 19 rows, and Pancake draws a polyline — at chart width that
	// is 23px straight chords across an S-curve, which reads as the line being
	// assembled from segments. Resampled to ~145 points for DRAWING only, with
	// monotone cubic interpolation so the cumulative curve can never dip or
	// exceed 1. Every published row is still exactly on the line; the percentile
	// below still comes from the original table via cdf().
	const curvePoints = resampleMonotone(points, (d) => +d.score, (d) => +d.inty, 8);

	$: percentile = complete ? Math.round(cdf(totalscore) * 100) : null;

	// ---- motion -----------------------------------------------------------
	// Respect reduced motion: zero-duration tweens, no transitions.
	const reduceMotion =
		typeof matchMedia !== 'undefined' &&
		matchMedia('(prefers-reduced-motion: reduce)').matches;
	const dur = (ms) => (reduceMotion ? 0 : ms);

	// One reveal, ~600ms: the text column rises, the curve fades in, the
	// patient's marker lands on it. Each element moves once. The curve is NOT
	// drawn on — see the .curve rule for why that was tried and removed.
	const shownScore = tweened(0, { duration: dur(520), easing: cubicOut });

	let resultEl;
	$: markerX = complete ? totalscore : 0;
	$: markerY = complete ? cdf(totalscore) : 0;
	$: if (complete) shownScore.set(totalscore);


	function revealResult(el) {
		// Bring the result into view inside the iframe without scrolling the
		// parent page ('nearest' keeps the effect local to this document).
		//
		// Deferred by a frame ON PURPOSE. At mount the panel has not been laid
		// out yet - the chart sizes itself afterwards - so scrolling here
		// measures a stale box and, on a phone, resolves to no scroll at all:
		// the patient answered the last question and got a result that was
		// entirely below the fold. Measured at 390px: scrollY stayed 0 with
		// the score clipped and the reading, chart and citation off-screen.
		// scrollIntoView is NOT used. block:'nearest' does nothing here - the
		// panel's top edge is already just inside the viewport (619px of 680),
		// so the browser considers it satisfied and the patient is left with a
		// clipped score and no visible reading. block:'start' would work but
		// can scroll ANCESTOR frames, moving the Webflow page around the
		// iframe. Setting scrollTop on this document does neither: it always
		// moves, and it can never escape into the parent.
		const frame = requestAnimationFrame(() => {
			const doc = el.ownerDocument;
			const win = doc.defaultView;
			const scroller = doc.scrollingElement || doc.documentElement;
			// Position is SET, not animated. A smooth scroll depends on the
			// frame rendering to completion; in a throttled iframe it can stall
			// at zero and strand the patient on a clipped score - measured
			// here, still 0 after 14s. The panel already fades and rises into
			// place, so the movement still reads as intentional.
			const top = el.getBoundingClientRect().top + win.scrollY - 12;
			scroller.scrollTop = Math.max(0, top);
		});
		return {
			destroy() {
				cancelAnimationFrame(frame);
			}
		};
	}

	$: markerState = [{ myX: markerX, myY: markerY }];
</script>

<div class="hfsgs">
	<header class="masthead">
		<h1>Hemifacial Spasm Grading&nbsp;Scale</h1>
		<!-- One line. The numbered steps and "n of 3 answered" already say the
		     rest; repeating it costs three lines of a 680px frame on a phone. -->
		<p class="lede">Three questions about the last month.</p>
	</header>

	<!-- Step 1 · Location -->
	<fieldset class="step">
		<legend class="step-head">
			<span class="step-no" class:done={q1 !== undefined}>
				{#if q1 !== undefined}<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6.5 5 9.5 10 3" /></svg>{:else}1{/if}
			</span>
			<span class="step-name">Location</span>
			<span class="step-ask">Where does the spasm occur?</span>
		</legend>
		<div class="choices">
			<label class="choice">
				<input type="radio" bind:group={q1} value={1} name="location" />
				<span class="mark" aria-hidden="true"></span>
				<span class="choice-text">Isolated to either the upper or lower part of the face</span>
			</label>
			<label class="choice">
				<input type="radio" bind:group={q1} value={2} name="location" />
				<span class="mark" aria-hidden="true"></span>
				<span class="choice-text">Involves the whole side of the face</span>
			</label>
		</div>
	</fieldset>

	<!-- Step 2 · Intensity -->
	<fieldset class="step">
		<legend class="step-head">
			<span class="step-no" class:done={q2 !== undefined}>
				{#if q2 !== undefined}<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6.5 5 9.5 10 3" /></svg>{:else}2{/if}
			</span>
			<span class="step-name">Intensity</span>
			<span class="step-ask">How strong is the spasm?</span>
		</legend>
		<div class="choices">
			<label class="choice">
				<input type="radio" bind:group={q2} value={1} name="intensity" />
				<span class="mark" aria-hidden="true"></span>
				<span class="choice-text">A single tic</span>
			</label>
			<label class="choice">
				<input type="radio" bind:group={q2} value={2} name="intensity" />
				<span class="mark" aria-hidden="true"></span>
				<span class="choice-text">Almost continuous spasms</span>
			</label>
		</div>
	</fieldset>

	<!-- Step 3 · Frequency -->
	<fieldset class="step">
		<legend class="step-head">
			<span class="step-no" class:done={q3 !== undefined}>
				{#if q3 !== undefined}<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6.5 5 9.5 10 3" /></svg>{:else}3{/if}
			</span>
			<span class="step-name">Frequency</span>
			<span class="step-ask">When does the spasm happen?</span>
		</legend>
		<div class="choices">
			<label class="choice">
				<input type="radio" bind:group={q3} value={1} name="frequency" />
				<span class="mark" aria-hidden="true"></span>
				<span class="choice-text">Only when provoked by facial movement</span>
			</label>
		</div>
		<!-- The remaining two options are one branch: the spasm is unprovoked,
		     graded by how often. The caption states the relationship the old
		     layout left implicit. -->
		<p class="branch-caption">Or — it happens by itself, unprovoked&hellip;</p>
		<div class="choices branch">
			<label class="choice">
				<input type="radio" bind:group={q3} value={3} name="frequency" />
				<span class="mark" aria-hidden="true"></span>
				<span class="choice-text">&hellip;less than half of the time</span>
			</label>
			<label class="choice">
				<input type="radio" bind:group={q3} value={5} name="frequency" />
				<span class="mark" aria-hidden="true"></span>
				<span class="choice-text">&hellip;more than half of the time</span>
			</label>
		</div>
	</fieldset>

	<!-- Result -->
	<div class="result-region" aria-live="polite">
		{#if !complete}
			<p class="progress-note" transition:fade={{ duration: dur(150) }}>
				{answered} of 3 answered
			</p>
		{:else}
			<section
				class="result"
				bind:this={resultEl}
				use:revealResult
				in:fade={{ duration: dur(140) }}
			>
				<div class="result-grid">
					<div class="scoreblock">
						<p class="result-label">Your score</p>
						<p class="score">
							<span class="score-n">{Math.round($shownScore)}</span>
							<span class="score-of">of 9</span>
						</p>
						<p class="reading">
							Higher than about <strong>{percentile}%</strong> of individuals
							with hemifacial spasm who received botulinum-toxin treatment.
						</p>
						<!-- The source sits WITH the claim, not under the chart. On a
						     phone the figcaption is ~240px further down, so a patient
						     reading only the score never saw what it was based on. -->
						<p class="source">
							Scale and population data:
							<cite>Tambasco&nbsp;et&nbsp;al., 2019</cite>
						</p>
					</div>

					<figure class="chartblock">
						<div class="chart">
							<Pancake.Chart x1={0} x2={9.5} y1={0} y2={1.08}>
								<Pancake.Grid horizontal count={3} let:value>
									<div class="grid-line horizontal"><span>{Math.round(value * 100)}%</span></div>
								</Pancake.Grid>
								<Pancake.Grid vertical count={9} let:value>
									<span class="x-label">{value}</span>
								</Pancake.Grid>
								<Pancake.Svg>
									<Pancake.SvgLine data={curvePoints} x={(d) => d.x} y={(d) => d.y} let:d>
										<path class="curve" {d} />
									</Pancake.SvgLine>
								</Pancake.Svg>
								<Pancake.Svg>
									<Pancake.SvgScatterplot data={markerState} x={(d) => d.myX} y={(d) => d.myY} let:d>
										<path class="marker-halo" {d} />
										<path class="marker" {d} />
									</Pancake.SvgScatterplot>
								</Pancake.Svg>
								<Pancake.Point x={markerX} y={markerY}>
									<span class="you-label">You</span>
								</Pancake.Point>
							</Pancake.Chart>
						</div>
						<figcaption>
							Share of treated patients at or below each score
						</figcaption>
					</figure>
				</div>
			</section>
		{/if}
	</div>
</div>

<style>
	/* Tokens now live on :root in public/global.css, shared by all four tools.
	   They were duplicated here until 2026-08-28; two definitions meant a token
	   change reached the other three tools and silently missed this one. */
	.hfsgs {
		max-width: 880px;
		margin: 0 auto;
		padding: var(--space-md) var(--space-md) var(--space-xl);
		color: var(--ink);
		font-family: 'Instrument Sans', -apple-system, BlinkMacSystemFont,
			'Segoe UI', Roboto, sans-serif;
		font-size: 16px;
		line-height: 1.5;
	}

	/* ---- masthead ------------------------------------------------------- */
	.masthead {
		margin-bottom: var(--space-lg);
	}
	h1 {
		margin: 0 0 var(--space-sm);
		font-size: clamp(24px, 3.2vw, 32px);
		font-weight: 650;
		line-height: 1.15;
		letter-spacing: -0.015em;
		color: var(--ink);
	}
	.lede {
		margin: 0;
		max-width: 52ch;
		color: var(--text-2);
	}

	/* ---- steps ---------------------------------------------------------- */
	.step {
		margin: 0 0 var(--space-lg);
		padding: 0;
		border: 0;
	}
	/* The name and the ask sit on one shared baseline, so the row stays
	   baseline-aligned. The badge is not text — baseline-aligning a circle
	   pushed it 5px low and needed a magic translateY that was only correct at
	   one font size. Pulling it to the line's cross-start instead, with the
	   name's line box set to the badge's height, centres it at any size. */
	.step-head {
		display: flex;
		align-items: baseline;
		gap: var(--space-sm);
		margin-bottom: var(--space-sm);
		padding: 0;
	}
	.step-no {
		flex: none;
		align-self: flex-start;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
		border-radius: 50%;
		border: 1.5px solid var(--line);
		font-size: 12px;
		font-weight: 600;
		color: var(--text-2);
		transition: background-color 0.25s cubic-bezier(0.22, 1, 0.36, 1),
			border-color 0.25s cubic-bezier(0.22, 1, 0.36, 1);
	}
	.step-no.done {
		background: var(--teal);
		border-color: var(--teal);
		color: #fff;
	}
	.step-no svg {
		width: 12px;
		height: 12px;
	}
	.step-no svg path {
		fill: none;
		stroke: #fff;
		stroke-width: 2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}
	.step-name {
		font-size: 18px;
		font-weight: 650;
		letter-spacing: -0.01em;
		/* 18 x 1.3333 = a 24px first line, exactly the badge's height. */
		line-height: 1.3333;
	}
	.step-ask {
		font-size: 14px;
		color: var(--text-2);
	}

	/* ---- choices -------------------------------------------------------- */
	.choices {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(260px, 100%), 1fr));
		/* align-items: start, explicitly. A grid's default is stretch, which is
		   invisible in Chrome because an auto row hugs its content — but on iOS
		   Safari the row can come out taller than its cards (seen on a real
		   iPhone: unanswered steps rendered with cards stretched to ~450px,
		   snapping to normal once answered). Start makes each card its own
		   content height whatever the row does, so the fix does not depend on
		   which WebKit quirk produced the tall row. */
		align-items: start;
		/* grid-auto-rows: max-content, explicitly. Reproduced in real WebKit
		   (Playwright, iPhone geometry, the live Webflow modal): on first layout
		   WebKit sized these auto rows to 216px and 118px for 60px and 44px
		   cards — no rule set a height, no child was tall, the track sizing
		   itself was wrong — and a click's relayout corrected it. Seen on a real
		   iPhone as a large blank gap between cards that vanished on tap.
		   max-content sizes each row to its tallest item's content through the
		   explicit path, which was correct on first paint in the same bench. */
		grid-auto-rows: max-content;
		gap: var(--space-sm);
		margin-left: 32px; /* aligns with step title, indents past the badge */
	}
	.branch-caption {
		margin: var(--space-sm) 0 var(--space-xs) 32px;
		font-size: 14px;
		font-style: italic;
		color: var(--text-2);
	}
	.choices.branch {
		margin-left: 48px; /* nested: these two belong to the caption above */
	}

	.choice {
		position: relative;
		display: flex;
		align-items: flex-start;
		gap: 10px;
		min-height: 44px; /* a real touch target */
		padding: 12px 14px;
		border: 1.5px solid var(--line);
		border-radius: 8px;
		background: var(--paper);
		cursor: pointer;
		transition: border-color 0.18s ease-out, background-color 0.18s ease-out;
	}
	.choice:hover {
		border-color: var(--teal);
	}
	.choice input {
		position: absolute;
		opacity: 0;
		pointer-events: none;
		margin: 0;
	}
	.mark {
		flex: none;
		width: 18px;
		height: 18px;
		margin-top: 2px;
		border-radius: 50%;
		border: 1.5px solid var(--line);
		background: #fff;
		transition: border-color 0.18s ease-out, box-shadow 0.18s ease-out;
	}
	.choice input:checked ~ .mark {
		border-color: var(--teal);
		box-shadow: inset 0 0 0 4.5px var(--teal);
	}
	.choice input:checked ~ .choice-text {
		color: var(--teal-deep);
		font-weight: 600;
	}
	.hfsgs .choice:has(input:checked) {
		border-color: var(--teal);
		background: var(--wash);
	}
	.choice input:focus-visible ~ .mark {
		outline: 2px solid var(--teal);
		outline-offset: 2px;
	}
	.choice-text {
		font-size: 15px;
		line-height: 1.4;
	}

	/* ---- result --------------------------------------------------------- */
	.result-region {
		margin-top: var(--space-lg);
	}
	.progress-note {
		margin: 0 0 0 32px;
		font-size: 13px;
		letter-spacing: 0.04em;
		color: var(--text-2);
	}

	.result {
		border-top: 1px solid var(--line);
		padding-top: var(--space-lg);
	}
	.result-grid {
		display: grid;
		grid-template-columns: minmax(240px, 320px) 1fr;
		gap: var(--space-xl);
		align-items: start;
	}
	@media (max-width: 640px) {
		.result-grid {
			grid-template-columns: 1fr;
			gap: var(--space-lg);
		}
	}

	.result-label {
		margin: 0;
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.14em;
		text-transform: uppercase;
		color: var(--teal-text);
	}
	.score {
		margin: 2px 0 var(--space-sm);
		display: flex;
		align-items: baseline;
		gap: 8px;
	}
	.score-n {
		font-size: 64px;
		font-weight: 700;
		line-height: 1;
		letter-spacing: -0.03em;
		font-variant-numeric: tabular-nums;
		color: var(--ink);
	}
	.score-of {
		font-size: 16px;
		font-weight: 500;
		color: var(--text-2);
	}
	.reading {
		margin: 0 0 var(--space-md);
		max-width: 34ch;
		font-size: 16px;
		line-height: 1.55;
	}
	.reading strong {
		color: var(--teal-deep);
		font-weight: 700;
	}

	/* The credential. Quiet on purpose - an understated citation reads as
	   more credible than a loud one, and this is the only trust signal the
	   result carries now. */
	.source {
		margin: var(--space-md) 0 0;
		padding-top: var(--space-sm);
		border-top: 1px solid var(--line);
		font-size: 12px;
		line-height: 1.45;
		color: var(--text-2);
	}
	.source cite {
		font-style: normal;
		font-weight: 600;
		color: var(--ink);
	}

	/* ---- the reveal ------------------------------------------------------
	   Two groups, not eight solos. Staggering every element separately
	   (0/60/150/260/700/820ms) alongside the score tween and the curve draw
	   gave ELEVEN distinct arrival moments over 1.24s, which reads as the
	   panel assembling itself in pieces. Rendering was a measured 60fps
	   throughout, so that feel was choreography, not jank.

	   Now the text column rises as one block, the curve draws, and the marker
	   lands while the line is still passing it. Three moments, ~600ms. */
	.result-label,
	.score,
	.reading,
	.source {
		animation: rise 300ms var(--ease-out) both;
	}
	/* The curve APPEARS; it is not drawn on. A stroke-dashoffset draw-on was
	   tried and removed: it set the dash inside requestAnimationFrame, so the
	   browser painted the full curve for one frame, hid it, and redrew it from
	   zero over 460ms — measured frame-by-frame as visible 1.0 -> 0.0 -> 1.0,
	   which the eye reads as "drawn, erased, redrawn". The original 2020 build
	   never animated the curve at all. A fade is one paint, no hidden frame. */
	/* Same TIMING as the text column, but its own keyframe. Two measured
	   traps here. (1) A separate opacity fade with an 80ms delay read as "text
	   fades, curve pops": the curve was at 0.05 while the text was at 0.80 on
	   the same frame. (2) Reusing the text's `rise` fixed that but its
	   translateY(6px) is in SVG user units inside Pancake's viewBox="0 0 100
	   100", so it scaled to 31px of on-screen travel — a jump, not a fade.
	   So: identical duration, easing and start, opacity only. Measured in
	   step with the text on every frame, zero travel. */
	.curve {
		animation: curve-in 300ms var(--ease-out) both;
	}
	@keyframes curve-in {
		from { opacity: 0; }
		to { opacity: 1; }
	}
	.marker,
	.marker-halo,
	.you-label,
	figcaption {
		animation: dot-in 260ms var(--ease-out) 340ms both;
	}
	.marker-halo {
		animation-name: halo-in;
	}
	.you-label {
		animation-name: label-in;
	}
	figcaption {
		animation-name: rise;
		animation-duration: 300ms;
	}
	@keyframes rise {
		from {
			opacity: 0;
			transform: translateY(6px);
		}
		to {
			opacity: 1;
			transform: none;
		}
	}
	@keyframes dot-in {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}
	@keyframes halo-in {
		from {
			opacity: 0;
		}
		to {
			opacity: 0.18;
		}
	}
	@keyframes label-in {
		from {
			opacity: 0;
			transform: translate(-50%, -28px);
		}
		to {
			opacity: 1;
			transform: translate(-50%, -34px);
		}
	}

	/* ---- chart ---------------------------------------------------------- */
	.chartblock {
		margin: 0;
	}
	.chart {
		position: relative;
		height: 230px;
		padding: 10px 8px 26px 40px;
	}
	.grid-line.horizontal {
		position: relative;
		display: block;
		width: 100%;
		border-bottom: 1px solid var(--line);
	}
	.grid-line.horizontal span {
		position: absolute;
		left: -36px;
		bottom: -7px;
		font-size: 11px;
		color: var(--text-2);
		font-variant-numeric: tabular-nums;
	}
	.x-label {
		position: absolute;
		width: 2em;
		left: -1em;
		bottom: -22px;
		font-size: 11px;
		text-align: center;
		color: var(--text-2);
		font-variant-numeric: tabular-nums;
	}
	.curve {
		stroke: var(--teal);
		stroke-width: 2px;
		stroke-linejoin: round;
		stroke-linecap: round;
		fill: none;
	}
	.marker-halo {
		stroke: var(--teal);
		stroke-width: 22px;
		stroke-linecap: round;
		opacity: 0.18;
		fill: none;
	}
	.marker {
		stroke: var(--teal-deep);
		stroke-width: 11px;
		stroke-linecap: round;
		fill: none;
	}
	.you-label {
		position: absolute;
		transform: translate(-50%, -34px);
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--teal-deep);
	}
	figcaption {
		margin-top: var(--space-sm);
		padding-left: 40px;
		font-size: 12px;
		color: var(--text-2);
	}

	/* ---- phones ----------------------------------------------------------
	   The live embed is <iframe width="100%" style="height:680px"> (verified
	   against the Webflow page), so a phone renders this at ~360-414px inside
	   a FIXED 680px frame. Before this block the initial state was 870px at
	   375px wide - the patient had to find a nested scroll region to reach
	   question 3. Every value here buys vertical space: the cards go
	   full-bleed (the badge indent costs 32px of width, which costs lines),
	   the rhythm tightens, and the frame fits. */
	@media (max-width: 480px) {
		.hfsgs {
			padding: var(--space-sm) var(--space-md) var(--space-md);
			font-size: 15px;
		}
		.masthead {
			margin-bottom: var(--space-md);
		}
		h1 {
			font-size: 22px;
		}
		.lede {
			font-size: 14px;
		}
		.step {
			margin-bottom: var(--space-md);
		}
		.step-head {
			gap: var(--space-xs);
			margin-bottom: var(--space-xs);
		}
		.step-no {
			width: 20px;
			height: 20px;
			font-size: 11px;
		}
		.step-name {
			/* 16 x 1.25 = 20px, matching the badge again at this size. */
			font-size: 16px;
			line-height: 1.25;
		}
		.step-ask {
			font-size: 13px;
		}
		/* Full-bleed cards: the 32px badge indent is worth more as text width
		   than as alignment once the column is this narrow. */
		.choices {
			gap: 6px;
			margin-left: 0;
		}
		.choices.branch {
			margin-left: var(--space-md);
		}
		.branch-caption {
			margin: 6px 0 var(--space-xs) 0;
			font-size: 13px;
		}
		.choice {
			padding: 9px 12px;
			gap: 8px;
		}
		.choice-text {
			font-size: 14px;
		}
		.mark {
			width: 16px;
			height: 16px;
		}
		.result-region {
			margin-top: var(--space-md);
		}
		.progress-note {
			margin-left: 0;
		}
		.result {
			padding-top: var(--space-md);
		}
		.score-n {
			font-size: 52px;
		}
		.chart {
			height: 190px;
		}
	}

	/* ---- the short frame ------------------------------------------------
	   There are TWO live embeds, not one. The calculator pages give this tool
	   680px of height; the three condition pages open it in a Webflow modal at
	   height="600px" and, on a 390px phone, only 351px of width. Measured on
	   the live site 2026-08-28 — see docs/embed-clipping.json and
	   tests/modal-embed-harness.html.

	   At 600px the initial state overflowed by 55px (351w) and 90px (320w),
	   pushing the last answer below the fold of an iframe that scrolls inside
	   a modal that also scrolls. So: key the compact pass on HEIGHT. The 680px
	   embed never matches it and keeps its comfortable spacing; the modal gets
	   a layout that fits. Touch targets stay 44px — the padding gives way, the
	   target does not.

	   The width arm catches the other overflow: at 320px the tool measured 690
	   even in the tall frame, so a 320-class phone was clipped by 10px on the
	   calculator pages too. 344px is the cut — 360px still fits comfortably
	   (629) and keeps the roomier type. */
	/* The gate is :global(html:not([data-autoheight])). Svelte scopes every
	   selector by appending a hash class to each element it names; html is not
	   in this component, so without :global() the compiler dropped every rule
	   in this block as unused — silently, no warning — and the fixed-frame
	   fallback stopped compacting. Caught by the regression sweep, not by eye. */
	@media (max-height: 660px), (max-width: 344px) {
		:global(html:not([data-autoheight])) .hfsgs {
			padding: 4px var(--space-sm) 6px;
			font-size: 15px;
		}
		:global(html:not([data-autoheight])) .masthead {
			margin-bottom: 6px;
		}
		:global(html:not([data-autoheight])) h1 {
			font-size: 20px;
			line-height: 1.1;
		}
		:global(html:not([data-autoheight])) .lede {
			font-size: 13px;
		}
		:global(html:not([data-autoheight])) .step {
			margin-bottom: 8px;
		}
		:global(html:not([data-autoheight])) .step-head {
			margin-bottom: 2px;
		}
		/* Keeps "Frequency" and its question on one line at 320px, which is
		   worth 20px — the head wraps to two lines without it. */
		:global(html:not([data-autoheight])) .step-ask {
			font-size: 12px;
		}
		:global(html:not([data-autoheight])) .choices {
			gap: 3px;
		}
		:global(html:not([data-autoheight])) .choice {
			padding: 7px 10px;
		}
		:global(html:not([data-autoheight])) .branch-caption {
			margin: 3px 0 2px 0;
			font-size: 12px;
		}
		:global(html:not([data-autoheight])) .result-region {
			margin-top: var(--space-sm);
		}
		:global(html:not([data-autoheight])) .result {
			padding-top: var(--space-sm);
		}
		:global(html:not([data-autoheight])) .score-n {
			font-size: 44px;
		}
		:global(html:not([data-autoheight])) .chart {
			height: 150px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.step-no,
		.choice,
		.mark {
			transition: none;
		}
		/* State, not choreography: every element lands at its final value. */
		.curve,
		.result-label,
		.score,
		.reading,
		.source,
		figcaption,
		.marker,
		.you-label {
			animation: none;
			opacity: 1;
			transform: none;
		}
		.you-label {
			transform: translate(-50%, -34px);
		}
		.marker-halo {
			animation: none;
			opacity: 0.18;
		}
	}
</style>
